import React from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  getEvolutionTabLobbyRows,
  getIndianGamesTabLobbyRows,
  getLobbyGroupByGroupId,
  getLobbyGroupForTab,
} from '../../constants/lotusCasinoLobbyGroups'
import { getLotusTabByIndex } from '../../constants/lotusCasinoTabs'
import { useCasinoUi } from '../../context/CasinoUiContext'
import {
  useLotusCategoryGames,
  useLotusLobbyGroupGames,
} from '../../hooks/useLotusLobbyGroupGames'
import { getGameId, type CasinoGameRecord } from '../../utils/lotusCasinoGameFilter'
import LotusCasinoGameCard from '../LotusCasinoGameCard/LotusCasinoGameCard'
import LotusCasinoLobbyRow from '../LotusCasinoLobby/LotusCasinoLobbyRow'
import '../LotusCasinoLobby/LotusCasinoLobby.css'
import './LotusCasinoTabGames.css'

export type LotusCasinoTabGamesProps = {
  tabIndex: number
  preLoginMode?: boolean
  groupId?: number | null
  onMoreGames?: (tabIndex: number, groupId?: number) => void
}

const TAB_GRID_LIMIT = 60

const LotusCasinoTabGames: React.FC<LotusCasinoTabGamesProps> = ({
  tabIndex,
  preLoginMode = false,
  groupId: groupIdProp,
  onMoreGames,
}) => {
  const [searchParams] = useSearchParams()
  const groupIdParam = searchParams.get('groupID')
  const parsedGroupId =
    groupIdProp ??
    (groupIdParam != null && groupIdParam !== '' ? parseInt(groupIdParam, 10) : null)
  const groupId = Number.isFinite(parsedGroupId as number) ? (parsedGroupId as number) : null

  const tab = getLotusTabByIndex(tabIndex)
  const lobbyGroup =
    groupId != null
      ? getLobbyGroupByGroupId(groupId)
      : getLobbyGroupForTab(tabIndex) ?? (tabIndex === 15 ? getLobbyGroupByGroupId(0) : undefined)

  const { playGame } = useCasinoUi()

  const lobbyResult = useLotusLobbyGroupGames(lobbyGroup ?? null, {
    preLoginMode,
    maxGames: TAB_GRID_LIMIT,
  })

  const categoryTarget =
    tab?.targetType === 'category' ? tab.target : tab?.defaultCategory ?? null
  const casinoType =
    tab?.casinoType ?? (tab?.targetType === 'category' ? 'slots' : 'slots')

  const categoryResult = useLotusCategoryGames(
    lobbyGroup ? null : categoryTarget,
    casinoType,
    { preLoginMode },
  )

  const games: CasinoGameRecord[] = lobbyGroup ? lobbyResult.games : categoryResult.games
  const loading = lobbyGroup ? lobbyResult.loading : categoryResult.loading
  const showTitle = !lobbyGroup
  const title = tab?.title ?? 'Games'

  if (tabIndex === 3 && groupId == null) {
    const evolutionRows = getEvolutionTabLobbyRows()
    return (
      <div className="lotus-casino-tab-games lotus-casino-tab-games--grouped">
        {evolutionRows.map((group) => (
          <LotusCasinoLobbyRow
            key={group.id}
            group={group}
            preLoginMode={preLoginMode}
            maxGames={TAB_GRID_LIMIT}
            showMoreCard={false}
            mobileLobbyLayout
            onMoreGames={onMoreGames}
          />
        ))}
      </div>
    )
  }

  // Indian Games (Mac88) — same subcategory rows pattern as Evolution
  if (tabIndex === 4 && groupId == null) {
    const indianRows = getIndianGamesTabLobbyRows()
    return (
      <div className="lotus-casino-tab-games lotus-casino-tab-games--grouped">
        {indianRows.map((group) => (
          <LotusCasinoLobbyRow
            key={group.id}
            group={group}
            preLoginMode={preLoginMode}
            maxGames={TAB_GRID_LIMIT}
            showMoreCard={false}
            mobileLobbyLayout
            onMoreGames={onMoreGames}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="lotus-casino-tab-games">
      {showTitle && <div className="lotus-casino-tab-games__title">{title}</div>}
      <div className="lotus-casino-tab-games__grid group">
        {loading &&
          games.length === 0 &&
          Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="lotus-casino-tab-games__skeleton" />
          ))}
        {games.map((game, index) => (
          <LotusCasinoGameCard
            key={getGameId(game) || index}
            game={game}
            onPlay={playGame}
            compact
            imagePriority={index < 8}
            showFavorite={!preLoginMode}
          />
        ))}
        {!loading && games.length === 0 && (
          <div className="lotus-casino-tab-games__empty">No games found in this category.</div>
        )}
      </div>
    </div>
  )
}

export default LotusCasinoTabGames
